import { useQuery } from '@tanstack/react-query';
import type { Screen } from '@/db/schema';
import { apiClient } from '@/lib/api-client';
import { ac, can } from '@/lib/permissions';
import { useCurrentUser } from './auth';
import { createCrudHooks } from './create-crud-hooks';

export const {
  keys: screenKeys,
  useList: useScreens,
  useDetail: useScreen,
  useCreate: useCreateScreen,
  useUpdate: useUpdateScreen,
  useDelete: useDeleteScreen,
} = createCrudHooks(apiClient.screens, 'screens');

export function useScreenByResource(resource: string) {
  return useQuery({
    queryKey: [...screenKeys.all, 'by-resource', resource],
    queryFn: async () => {
      const res = await apiClient.screens['by-resource']({ resource }).get();
      return res.data;
    },
    enabled: !!resource,
  });
}

/**
 * Aproximação client-side pra mostrar/esconder botões numa screen dinâmica, a partir da
 * mesma regra default usada em `syncScreenPermissions` (admin: tudo que a screen habilita;
 * user: browse+read se habilitados). Quem decide de verdade é sempre o backend
 * (`canWithDynamic`, contra a tabela `dynamic_permissions`) — isto é só UX.
 */
export function screenAllowsAction(
  screen: Pick<Screen, 'actions' | 'resource'> | undefined | null,
  role: string | undefined,
  action: string,
) {
  if (!role || !screen?.actions?.includes(action)) return false;
  if (role === 'admin') return true;

  // Resources compilados (users/customers/sales/...) têm permissão real no `ac` estático —
  // consulta ela em vez da aproximação abaixo, senão o botão fica escondido mesmo com
  // permissão concedida (ex: usuário padrão com `create` em customers).
  if (screen.resource in ac.statements) {
    return can(role as any, screen.resource, action);
  }

  return action === 'browse' || action === 'read';
}

export function useScreenPermissions(screen?: Screen | null) {
  const { data } = useCurrentUser();
  const role = data?.user.role;

  return {
    canBrowse: screenAllowsAction(screen, role, 'browse'),
    canCreate: screenAllowsAction(screen, role, 'create'),
    canRead: screenAllowsAction(screen, role, 'read'),
    canUpdate: screenAllowsAction(screen, role, 'update'),
    canDelete: screenAllowsAction(screen, role, 'delete'),
  };
}
